import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';
import { standardLegend, standardTitle } from '../utils/chartFormatting';

export const CHART_COLORS = {
  ev: {
    primary: '#3b82f6',
    light: 'rgba(59, 130, 246, 0.7)',
    dark: '#1e40af'
  },
  charging: {
    primary: '#10b981',
    light: 'rgba(16, 185, 129, 0.7)',
    dark: '#047857'
  },
  scatter: {
    primary: '#8b5cf6',
    light: 'rgba(139, 92, 246, 0.6)',
    dark: '#6d28d9'
  },
  quintiles: [
    '#ef4444',
    '#f59e0b',
    '#eab308',
    '#10b981',
    '#3b82f6'
  ]
} as const;

export interface TractEVData {
  tract: number;
  year: number;
  median_income: number;
  evs_per_100_people: number;
  total_population: number;
  pct_bachelor_or_higher: number;
  pct_owner_occupied: number;
  county: string;
}

export interface IncomeQuintile {
  quintile: number;
  incomeRange: { min: number; max: number };
  tractCount: number;
  avgIncome: number;
  avgEVAdoption: number;
  totalPopulation: number;
  avgEducation: number;
  avgHomeownership: number;
}

export interface EVBarriersMetrics {
  correlation: number;
  rSquared: number;
  sampleSize: number;
  avgIncome: number;
  avgEVAdoption: number;
  evGap: number;
  quintileGap: number;
}

export interface EVBarriersData {
  quintiles: IncomeQuintile[];
  tracts: TractEVData[];
  county: string;
  metrics: EVBarriersMetrics;
  barChartData: any;
  barChartOptions: any;
  scatterChartData: any;
  scatterChartOptions: any;
  narrative: string;
  location: string;
}

export class EVAdoptionBarriersService {
  static async getData(location: string): Promise<EVBarriersData> {
    await supabaseLocationService.initialize();
    const county = supabaseLocationService.getCountyForCity(location) || location;

    if (!county) {
      throw new Error(`County not found for location: ${location}`);
    }

    const tractsRaw = await this.fetchEVTractData(county);
    const tracts = this.transformTractData(tractsRaw, county);

    if (tracts.length === 0) {
      throw new Error(`No EV adoption data found for ${county}`);
    }

    const quintiles = this.stratifyByQuintiles(tracts);
    const metrics = this.calculateMetrics(tracts, quintiles);
    const narrative = this.generateNarrative(county, metrics, quintiles);

    const barChartData = this.generateBarChartData(quintiles);
    const barChartOptions = this.generateBarChartOptions(county);
    const scatterChartData = this.generateScatterChartData(tracts);
    const uniqueYears = Array.from(new Set(tracts.map((t) => t.year).filter((y) => Number.isFinite(y))));
    const scatterChartOptions = this.generateScatterChartOptions(county, uniqueYears.length > 1);

    return {
      quintiles,
      tracts,
      county,
      metrics,
      barChartData,
      barChartOptions,
      scatterChartData,
      scatterChartOptions,
      narrative,
      location
    };
  }

  private static async fetchEVTractData(county: string): Promise<any[]> {
    // Query by county directly to avoid massive tract IN(...) URLs that can trigger 400s.
    const cleanCounty = county.replace(/ County$/i, '').toUpperCase();

    const { data: evData, error: evError } = await supabaseClient
      .from('ev_by_tract_2023')
      .select('tract, median_income, evs_per_100_people, total_population, pct_bachelor_or_higher, pct_owner_occupied, "County"')
      .eq('County', cleanCounty)
      .not('median_income', 'is', null)
      .not('evs_per_100_people', 'is', null)
      .gt('total_population', 0);

    if (evError) {
      throw new Error(`Failed to fetch EV data: ${evError.message}`);
    }

    return evData || [];
  }

  private static transformTractData(rawData: any[], county: string): TractEVData[] {
    return rawData
      .map(row => ({
        tract: parseInt(row.tract.toString()),
        year: 2023,
        median_income: parseFloat(row.median_income.toString()) || 0,
        evs_per_100_people: parseFloat(row.evs_per_100_people.toString()) || 0,
        total_population: parseFloat(row.total_population.toString()) || 0,
        pct_bachelor_or_higher: parseFloat(row.pct_bachelor_or_higher?.toString() || '0') || 0,
        pct_owner_occupied: parseFloat(row.pct_owner_occupied?.toString() || '0') || 0,
        county: row.County || county
      }))
      .filter(tract => tract.median_income > 0 && tract.evs_per_100_people >= 0);
  }

  private static stratifyByQuintiles(tracts: TractEVData[]): IncomeQuintile[] {
    const sorted = [...tracts].sort((a, b) => a.median_income - b.median_income);
    const quintileSize = Math.floor(sorted.length / 5);
    const quintiles: IncomeQuintile[] = [];

    for (let i = 0; i < 5; i++) {
      const start = i * quintileSize;
      const end = i === 4 ? sorted.length : (i + 1) * quintileSize;
      const quintileTracts = sorted.slice(start, end);

      if (quintileTracts.length === 0) continue;

      const incomes = quintileTracts.map(t => t.median_income);
      const evs = quintileTracts.map(t => t.evs_per_100_people);
      const populations = quintileTracts.map(t => t.total_population);
      const educations = quintileTracts.map(t => t.pct_bachelor_or_higher);
      const homeownerships = quintileTracts.map(t => t.pct_owner_occupied);

      quintiles.push({
        quintile: i + 1,
        incomeRange: {
          min: Math.min(...incomes),
          max: Math.max(...incomes)
        },
        tractCount: quintileTracts.length,
        avgIncome: incomes.reduce((a, b) => a + b, 0) / quintileTracts.length,
        avgEVAdoption: evs.reduce((a, b) => a + b, 0) / quintileTracts.length,
        totalPopulation: populations.reduce((a, b) => a + b, 0),
        avgEducation: educations.reduce((a, b) => a + b, 0) / quintileTracts.length,
        avgHomeownership: homeownerships.reduce((a, b) => a + b, 0) / quintileTracts.length
      });
    }

    return quintiles;
  }

  private static calculateMetrics(
    tracts: TractEVData[],
    quintiles: IncomeQuintile[]
  ): EVBarriersMetrics {
    const n = tracts.length;
    const sumX = tracts.reduce((sum, tract) => sum + tract.median_income, 0);
    const sumY = tracts.reduce((sum, tract) => sum + tract.evs_per_100_people, 0);
    const sumXY = tracts.reduce(
      (sum, tract) => sum + tract.median_income * tract.evs_per_100_people,
      0
    );
    const sumXX = tracts.reduce(
      (sum, tract) => sum + tract.median_income * tract.median_income,
      0
    );
    const sumYY = tracts.reduce(
      (sum, tract) => sum + tract.evs_per_100_people * tract.evs_per_100_people,
      0
    );

    const correlation =
      (n * sumXY - sumX * sumY) /
      Math.sqrt((n * sumXX - sumX * sumX) * (n * sumYY - sumY * sumY));
    const rSquared = correlation * correlation;

    const avgIncome = sumX / n;
    const avgEVAdoption = sumY / n;

    const q1 = quintiles[0];
    const q5 = quintiles[quintiles.length - 1];
    const evGap = q5.avgEVAdoption - q1.avgEVAdoption;
    const quintileGap = q5.avgIncome - q1.avgIncome;

    return {
      correlation: correlation || 0,
      rSquared: rSquared || 0,
      sampleSize: n,
      avgIncome: avgIncome || 0,
      avgEVAdoption: avgEVAdoption || 0,
      evGap: evGap || 0,
      quintileGap: quintileGap || 0
    };
  }

  private static generateNarrative(
    county: string,
    metrics: EVBarriersMetrics,
    quintiles: IncomeQuintile[]
  ): string {
    const correlationStrength =
      metrics.correlation > 0.7
        ? 'strong positive'
        : metrics.correlation > 0.3
        ? 'moderate positive'
        : metrics.correlation > -0.3
        ? 'weak'
        : metrics.correlation > -0.7
        ? 'moderate negative'
        : 'strong negative';

    const q1 = quintiles[0];
    const q5 = quintiles[quintiles.length - 1];

    return `County-level analysis of ${metrics.sampleSize} census tracts in ${county} reveals a ${correlationStrength} correlation ` +
      `(r = ${metrics.correlation.toFixed(3)}) between household income and EV adoption. ` +
      `The highest-income quintile (median: $${q5.avgIncome.toLocaleString()}) has ${q5.avgEVAdoption.toFixed(2)} EVs per 100 people, ` +
      `compared to ${q1.avgEVAdoption.toFixed(2)} EVs per 100 people in the lowest-income quintile (median: $${q1.avgIncome.toLocaleString()}), ` +
      `representing an EV adoption gap of ${metrics.evGap.toFixed(2)} EVs per 100 people. ` +
      `This disparity highlights economic barriers to EV adoption and underscores the need for targeted incentives and infrastructure ` +
      `investments in lower-income communities to achieve equitable transportation electrification.`;
  }

  private static generateBarChartData(quintiles: IncomeQuintile[]): any {
    const labels = quintiles.map(q => `Q${q.quintile}`);

    return {
      labels,
      datasets: [
        {
          label: 'EVs per 100 People',
          data: quintiles.map(q => q.avgEVAdoption),
          backgroundColor: CHART_COLORS.quintiles,
          borderColor: CHART_COLORS.quintiles.map(c => c),
          borderWidth: 2,
          yAxisID: 'y'
        }
      ]
    };
  }

  private static generateBarChartOptions(county: string): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      backgroundColor: '#ffffff',
      plugins: {
        title: {
          ...standardTitle(`EV Adoption by Income - ${county} (County-Level)`),
          color: '#1f2937'
        },
        legend: {
          ...standardLegend,
          labels: { ...standardLegend.labels, color: '#374151' }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => `EVs per 100 People: ${context.parsed.y.toFixed(2)}`
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Income Quintile (Q1=Lowest, Q5=Highest)',
            font: { size: 14, weight: 'bold' as const },
            color: '#374151'
          },
          ticks: { color: '#374151' },
          grid: { display: true, color: 'rgba(0, 0, 0, 0.08)' }
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'EVs per 100 People',
            font: { size: 14, weight: 'bold' as const },
            color: '#374151'
          },
          ticks: { color: '#374151' },
          grid: { display: true, color: 'rgba(0, 0, 0, 0.08)' }
        }
      }
    };
  }

  private static generateScatterChartData(tracts: TractEVData[]): any {
    const scatterPoints = tracts.map(tract => ({
      x: tract.median_income,
      y: tract.evs_per_100_people,
      year: tract.year,
      population: tract.total_population,
      education: tract.pct_bachelor_or_higher,
      homeownership: tract.pct_owner_occupied,
      tract: tract.tract
    }));

    const n = tracts.length;
    const sumX = tracts.reduce((sum, t) => sum + t.median_income, 0);
    const sumY = tracts.reduce((sum, t) => sum + t.evs_per_100_people, 0);
    const sumXY = tracts.reduce((sum, t) => sum + t.median_income * t.evs_per_100_people, 0);
    const sumXX = tracts.reduce((sum, t) => sum + t.median_income * t.median_income, 0);
    const sumYY = tracts.reduce((sum, t) => sum + t.evs_per_100_people * t.evs_per_100_people, 0);

    const avgX = sumX / n;
    const avgY = sumY / n;
    const correlation =
      (n * sumXY - sumX * sumY) /
      Math.sqrt((n * sumXX - sumX * sumX) * (n * sumYY - sumY * sumY));

    const slope =
      correlation *
      (Math.sqrt(sumYY / n - (sumY / n) ** 2) / Math.sqrt(sumXX / n - (sumX / n) ** 2));
    const intercept = avgY - slope * avgX;

    const minX = Math.min(...scatterPoints.map(d => d.x));
    const maxX = Math.max(...scatterPoints.map(d => d.x));
    const trendLineData = [
      { x: minX, y: slope * minX + intercept },
      { x: maxX, y: slope * maxX + intercept }
    ];

    return {
      datasets: [
        {
          label: 'Census Tracts',
          data: scatterPoints,
          type: 'scatter' as const,
          backgroundColor: CHART_COLORS.scatter.light,
          borderColor: CHART_COLORS.scatter.dark,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointHoverBackgroundColor: CHART_COLORS.scatter.primary,
          pointHoverBorderColor: CHART_COLORS.scatter.dark,
          pointHoverBorderWidth: 2,
          showLine: false,
          tension: 0
        },
        {
          label: 'Trend Line',
          data: trendLineData,
          type: 'line' as const,
          borderColor: '#dc2626',
          backgroundColor: 'rgba(220, 38, 38, 0.1)',
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
          tension: 0
        }
      ]
    };
  }

  private static generateScatterChartOptions(county: string, includeYearInTooltip: boolean): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      backgroundColor: '#ffffff',
      plugins: {
        title: {
          ...standardTitle(`Income vs EV Adoption Correlation - ${county} (County-Level)`),
          color: '#1f2937'
        },
        legend: {
          ...standardLegend,
          labels: { ...standardLegend.labels, color: '#374151' }
        },
        tooltip: {
          callbacks: {
            title: (context: any) => {
              const point = context[0].raw;
              return `Tract ${point.tract}`;
            },
            label: (context: any) => {
              const point = context.raw;
              const lines = [
                `Median Income: $${point.x.toLocaleString()}`,
                `EVs per 100 People: ${point.y.toFixed(2)}`,
                `Population: ${point.population.toLocaleString()}`,
                `Bachelor's+: ${(point.education * 100).toFixed(1)}%`,
                `Homeownership: ${(point.homeownership * 100).toFixed(1)}%`
              ];
              if (includeYearInTooltip && point.year !== undefined && point.year !== null) {
                lines.splice(2, 0, `Year: ${point.year}`);
              }
              return lines;
            }
          }
        }
      },
      scales: {
        x: {
          type: 'linear',
          title: {
            display: true,
            text: 'Median Household Income ($)',
            font: { size: 14, weight: 'bold' as const },
            color: '#374151'
          },
          ticks: {
            color: '#374151',
            callback: (value: any) => {
              if (typeof value === 'number') {
                return `$${value.toLocaleString()}`;
              }
              return value;
            },
            stepSize: 10000,
            maxTicksLimit: 8
          },
          grid: {
            display: true,
            color: 'rgba(0, 0, 0, 0.08)'
          }
        },
        y: {
          title: {
            display: true,
            text: 'EVs per 100 People',
            font: { size: 14, weight: 'bold' as const },
            color: '#374151'
          },
          ticks: { color: '#374151' },
          grid: { display: true, color: 'rgba(0, 0, 0, 0.08)' },
          beginAtZero: true
        }
      }
    };
  }
}
